import React, { useState } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * TemplateDownload - Reusable component for downloading sample upload templates
 * 
 * @param {string} templateType - Type of template: vehicles, repairs, mr-report, mileage, fuel, servicing, tyres
 * @param {string} token - Auth token for API calls
 * @param {string} label - Optional custom label (defaults to "Download Sample Template")
 * @param {boolean} compact - If true, shows a single button with dropdown
 */
function TemplateDownload({ templateType, token, label, compact = false }) {
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [message, setMessage] = useState('');

  const downloadTemplate = async (format) => {
    setLoading(true);
    setMessage('');
    setShowDropdown(false);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/templates/${templateType}/${format}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to download template');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${templateType}_upload_template.${format === 'excel' ? 'xlsx' : 'csv'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      
      setMessage(`✅ ${format.toUpperCase()} template downloaded!`);
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Download error:', error);
      setMessage(`❌ Failed to download template`);
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const buttonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 0.75rem',
    fontSize: '0.8rem',
    background: '#EEF2FF',
    color: '#4F46E5',
    border: '1px solid #C7D2FE',
    borderRadius: '6px',
    cursor: loading ? 'wait' : 'pointer',
    transition: 'all 0.2s ease',
    fontWeight: '500'
  };

  const hoverStyle = {
    background: '#E0E7FF'
  };

  if (compact) {
    // Single button with dropdown
    return (
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          onMouseEnter={(e) => Object.assign(e.target.style, hoverStyle)}
          onMouseLeave={(e) => Object.assign(e.target.style, { background: '#EEF2FF' })}
          style={buttonStyle}
          disabled={loading}
          data-testid={`template-download-${templateType}`}
        >
          📥 {loading ? 'Downloading...' : (label || 'Sample Template')} ▾
        </button>
        
        {showDropdown && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: '4px',
            background: 'white',
            border: '1px solid #E2E8F0',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            zIndex: 100,
            minWidth: '140px',
            overflow: 'hidden'
          }}>
            <button
              onClick={() => downloadTemplate('excel')}
              style={{
                display: 'block',
                width: '100%',
                padding: '0.6rem 1rem',
                border: 'none',
                background: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                fontSize: '0.85rem'
              }}
              onMouseEnter={(e) => e.target.style.background = '#F8FAFC'}
              onMouseLeave={(e) => e.target.style.background = 'none'}
            >
              📊 Excel (.xlsx)
            </button>
            <button
              onClick={() => downloadTemplate('csv')}
              style={{
                display: 'block',
                width: '100%',
                padding: '0.6rem 1rem',
                border: 'none',
                background: 'none',
                textAlign: 'left',
                cursor: 'pointer',
                fontSize: '0.85rem'
              }}
              onMouseEnter={(e) => e.target.style.background = '#F8FAFC'}
              onMouseLeave={(e) => e.target.style.background = 'none'}
            >
              📄 CSV (.csv)
            </button>
          </div>
        )}
        
        {message && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: '4px',
            padding: '0.5rem 0.75rem',
            background: message.includes('✅') ? '#F0FDF4' : '#FEF2F2',
            border: `1px solid ${message.includes('✅') ? '#BBF7D0' : '#FECACA'}`,
            borderRadius: '4px',
            fontSize: '0.75rem',
            whiteSpace: 'nowrap',
            zIndex: 101
          }}>
            {message}
          </div>
        )}
        
        {/* Click outside to close */}
        {showDropdown && (
          <div 
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99 }}
            onClick={() => setShowDropdown(false)}
          />
        )}
      </div>
    );
  }

  // Two separate buttons
  return (
    <div style={{ display: 'inline-flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
      <span style={{ fontSize: '0.8rem', color: '#64748B', marginRight: '0.25rem' }}>
        {label || 'Download Template:'}
      </span>
      <button
        onClick={() => downloadTemplate('excel')}
        onMouseEnter={(e) => Object.assign(e.target.style, hoverStyle)}
        onMouseLeave={(e) => Object.assign(e.target.style, { background: '#EEF2FF' })}
        style={buttonStyle}
        disabled={loading}
        data-testid={`template-excel-${templateType}`}
      >
        📊 {loading ? '...' : 'Excel'}
      </button>
      <button
        onClick={() => downloadTemplate('csv')}
        onMouseEnter={(e) => Object.assign(e.target.style, hoverStyle)}
        onMouseLeave={(e) => Object.assign(e.target.style, { background: '#EEF2FF' })}
        style={buttonStyle}
        disabled={loading}
        data-testid={`template-csv-${templateType}`}
      >
        📄 {loading ? '...' : 'CSV'}
      </button>
      
      {message && (
        <span style={{
          fontSize: '0.75rem',
          color: message.includes('✅') ? '#059669' : '#DC2626'
        }}>
          {message}
        </span>
      )}
    </div>
  );
}

export default TemplateDownload;
